import { FormEvent, useEffect, useRef, useState } from "react";
import { Camera, Keyboard, QrCode, StopCircle } from "lucide-react";
import { request } from "./FunctionalApp";

export function QrScanWorkspace({
  onOpen,
  initialCode,
}: {
  onOpen: (type: string, id: string) => void;
  initialCode?: string;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const controls = useRef<{ stop: () => void } | null>(null);
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<any>(null);
  const [scanning, setScanning] = useState(false);

  function stop() {
    controls.current?.stop();
    controls.current = null;
    setScanning(false);
  }
  useEffect(() => stop, []);
  async function resolve(value: string) {
    const normalized = value.trim();
    if (!normalized) return;
    const response = await request(
      `/api/cmms/scan/resolve?code=${encodeURIComponent(normalized)}`,
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setResult(null);
      setMessage(
        String(body.error || "Code not recognised").replaceAll("_", " "),
      );
      return;
    }
    stop();
    setCode(normalized);
    setResult(body);
    setMessage("");
  }
  useEffect(() => {
    if (initialCode) resolve(initialCode);
  }, [initialCode]);
  async function start() {
    setMessage("");
    try {
      if (!video.current) return;
      const { BrowserQRCodeReader } = await import("@zxing/browser");
      const reader = new BrowserQRCodeReader();
      setScanning(true);
      controls.current = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: "environment" } }, audio: false },
        video.current,
        (found) => {
          if (found) resolve(found.getText());
        },
      );
    } catch {
      stop();
      setMessage(
        "Camera access was not available. Enter the printed QR value below.",
      );
    }
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    resolve(code);
  }
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Scan QR code</h1>
          <p>Open a part, work order or asset using its TaskRoute label.</p>
        </div>
      </div>
      <section className="func-panel simple-form">
        <div className="func-table-head">
          <h2>
            <QrCode /> Camera scanner
          </h2>
          {scanning ? (
            <button className="secondary" onClick={stop}>
              <StopCircle /> Stop camera
            </button>
          ) : (
            <button onClick={start}>
              <Camera /> Start camera
            </button>
          )}
        </div>
        <video
          ref={video}
          playsInline
          muted
          style={{
            width: "100%",
            maxHeight: 420,
            display: scanning ? "block" : "none",
            background: "#102235",
            borderRadius: 8,
          }}
        />
        <form onSubmit={submit}>
          <label className="wide">
            <Keyboard /> QR value
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="TR:PART:…, TR:WO:… or TR:ASSET:…"
            />
          </label>
          <button>Open code</button>
        </form>
        {message ? <p className="func-admin-note">{message}</p> : null}
        {result ? (
          <article>
            <span>
              <b>{result.label}</b>
              <small>{result.description}</small>
            </span>
            <button onClick={() => onOpen(result.type, result.id)}>
              Open {result.type.toLowerCase()}
            </button>
          </article>
        ) : null}
      </section>
    </>
  );
}
